/* Grubwick backend client for restaurant + menu data.
 *
 * Fetches from the /gw API (via gwFetch, which attaches the Supabase token) and
 * maps the raw API shapes (src/api/types.ts) onto the UI's Restaurant/MenuItemRecord types so
 * the admin views can stay unchanged. List/menu reads are public GETs; edits and
 * deletes are wired to the PATCH/DELETE endpoints below. The API has no create
 * (POST) endpoint yet, so brand-new restaurants/items stay in-memory only. */
import { gwFetch, GwApiError, apiErrorMessage } from "./client";
import type { Restaurant, MenuItemRecord, LocationRef } from "../data/adminData";
import type {
  ApiRestaurant,
  ApiRestaurantsResponse,
  ApiBrandLocation,
  ApiMenuItem,
  ApiLocationMenuResponse,
  RestaurantPatch,
  MenuItemPatch,
} from "./types";

/* Stand-in for fields the API does not yet provide (cuisine, neighborhood…). */
export const PLACEHOLDER = "Category";

/* Build the error for a failed write (mutating endpoints are role-gated), so a
   403 surfaces as "you don't have permission" rather than a raw status. `status`
   is preserved on the GwApiError for callers that branch on it. */
function writeError(status: number, fallback: string): GwApiError {
  return new GwApiError(status, apiErrorMessage(status, fallback));
}

export function formatPriceCents(cents: number | null): string {
  if (cents == null) return "—";
  return "$" + (cents / 100).toFixed(2);
}

/* Inverse of formatPriceCents: turn a UI price string ("$12.50", "12", "—", "")
   back into integer cents, or null when there is no usable number. */
export function parsePriceToCents(price: string): number | null {
  const cleaned = String(price).replace(/[^0-9.]/g, "");
  if (!cleaned) return null;
  const dollars = parseFloat(cleaned);
  if (isNaN(dollars)) return null;
  return Math.round(dollars * 100);
}

/* True for ids that came from the backend (plain integers). In-session records
   use newId('r'|'m') which prefixes a letter, so they fail this test and can't
   be persisted until a create endpoint exists. */
export function isPersistedId(id: string): boolean {
  return /^\d+$/.test(id);
}

export function mapApiLocation(api: ApiBrandLocation): LocationRef {
  return {
    id: String(api.id),
    name: api.name ?? "",
    address: api.address ?? "",
    city: api.city ?? "",
    itemCount: api.menu_item_count,
  };
}

export function mapApiRestaurant(api: ApiRestaurant): Restaurant {
  return {
    id: String(api.id),
    name: api.record_name,
    venue: "Restaurant", // API has no venue type yet — default to Restaurant.
    hood: api.city ?? PLACEHOLDER,
    street: api.address ?? "",
    city: api.city ?? "",
    state: api.state ?? "",
    zip: api.zip ?? "",
    style: PLACEHOLDER,
    dish: "",
    url: api.website_url ?? "",
    desc: "",
    locations: api.locations.map(mapApiLocation),
    menu: [], // Loaded per location from the location-menu endpoint on open.
  };
}

export function mapApiMenuItem(api: ApiMenuItem): MenuItemRecord {
  return {
    id: String(api.id),
    name: api.name,
    price: formatPriceCents(api.price_cents),
    category: api.section ?? PLACEHOLDER,
    desc: api.description ?? "",
    sectionId: api.section_id != null ? String(api.section_id) : null,
  };
}

export async function fetchRestaurants(): Promise<Restaurant[]> {
  const res = await gwFetch("/restaurants?limit=100");
  if (!res.ok) {
    throw new Error(`Failed to load restaurants (${res.status})`);
  }
  const data: ApiRestaurantsResponse = await res.json();
  return data.items.map(mapApiRestaurant);
}

export async function fetchLocationMenu(
  restaurantId: string,
  locationId: string,
): Promise<MenuItemRecord[]> {
  const res = await gwFetch(
    `/restaurants/${restaurantId}/locations/${locationId}/menu`,
  );
  if (!res.ok) {
    throw new Error(`Failed to load menu (${res.status})`);
  }
  const data: ApiLocationMenuResponse = await res.json();
  return data.location.items.map(mapApiMenuItem);
}

/* Map a UI Restaurant onto the PATCH body. Only fields with an API column are
   sent (venue/hood/style/dish/desc have none yet and stay local). Empty strings
   become null so the server clears the column rather than storing "". */
export function restaurantToPatch(r: Restaurant): RestaurantPatch {
  return {
    record_name: r.name,
    address: r.street || null,
    city: r.city || null,
    state: r.state || null,
    zip: r.zip || null,
    website_url: r.url || null,
  };
}

/* Map a UI MenuItemRecord onto the PATCH body. The "Category" placeholder means
   "no section", so it maps to null. */
export function menuItemToPatch(m: MenuItemRecord): MenuItemPatch {
  return {
    name: m.name,
    section: m.category && m.category !== PLACEHOLDER ? m.category : null,
    description: m.desc || null,
    price_cents: parsePriceToCents(m.price),
  };
}

export async function updateRestaurant(
  id: string,
  r: Restaurant,
): Promise<Restaurant> {
  const res = await gwFetch(`/restaurants/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(restaurantToPatch(r)),
  });
  if (!res.ok) {
    throw writeError(res.status, `Failed to save restaurant (${res.status})`);
  }
  const data: ApiRestaurant = await res.json();
  return mapApiRestaurant(data);
}

export async function deleteRestaurant(id: string): Promise<void> {
  const res = await gwFetch(`/restaurants/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw writeError(res.status, `Failed to delete restaurant (${res.status})`);
  }
}

export async function updateMenuItem(
  id: string,
  m: MenuItemRecord,
): Promise<MenuItemRecord> {
  const res = await gwFetch(`/menu_items/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(menuItemToPatch(m)),
  });
  if (!res.ok) {
    throw writeError(res.status, `Failed to save menu item (${res.status})`);
  }
  const data: ApiMenuItem = await res.json();
  return mapApiMenuItem(data);
}

export async function deleteMenuItem(id: string): Promise<void> {
  const res = await gwFetch(`/menu_items/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw writeError(res.status, `Failed to delete menu item (${res.status})`);
  }
}

/* Persist a new top-to-bottom order for a location's menu sections.
   `sectionIds` is the full ordered list of backend section ids (strings here,
   sent as integers). The uncategorized bucket has no id and is never included. */
export async function reorderSections(
  locationId: string,
  sectionIds: string[],
): Promise<void> {
  const res = await gwFetch(`/locations/${locationId}/sections/order`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ section_ids: sectionIds.map(Number) }),
  });
  if (!res.ok) {
    throw writeError(res.status, `Failed to reorder sections (${res.status})`);
  }
}
